import { useEffect, useRef, useState } from 'react';
import { accountApi } from '../../services/api';
import type { WalletAddressItem } from '../../services/api';
import { AppleEmoji } from '../AppleEmoji';
import { validateTrc20Address, type Trc20Validation } from '../../utils/tron';
import './WalletSelector.css';

interface Props {
  value: string;
  onChange: (address: string) => void;
  enabled: boolean; // user is authenticated
  network?: string;
}

export default function WalletSelector({ value, onChange, enabled, network = 'TRC-20' }: Props) {
  const [wallets, setWallets] = useState<WalletAddressItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [validation, setValidation] = useState<Trc20Validation | null>(null);
  const [touched, setTouched] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    accountApi
      .wallets()
      .then(({ data }) => {
        const filtered = data.filter((w) => w.network === network);
        setWallets(filtered);
        // Auto-select default / most recent if value is empty
        if (!value && filtered.length > 0) {
          const def = filtered.find((w) => w.is_default) || filtered[0];
          onChange(def.address);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, network]);

  // Live validation — only after user has interacted
  useEffect(() => {
    if (!value) {
      setValidation(null);
      return;
    }
    let cancelled = false;
    validateTrc20Address(value).then((res) => {
      if (!cancelled) setValidation(res);
    });
    return () => {
      cancelled = true;
    };
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pick = (addr: string) => {
    onChange(addr);
    setTouched(true);
    setOpen(false);
  };

  const hasSaved = enabled && wallets.length > 0;
  const showError = touched && validation && !validation.valid && value.length > 0;
  const showValid = touched && validation?.valid;

  const wrapperClass =
    'input-wrapper' +
    (showError ? ' input-wrapper--invalid' : '') +
    (showValid ? ' input-wrapper--valid' : '');

  return (
    <div className="wallet-selector" ref={rootRef}>
      <div className={wrapperClass}>
        <input
          type="text"
          className="input"
          placeholder="Адрес кошелька TRC-20"
          value={value}
          onChange={(e) => {
            onChange(e.target.value.trim());
            if (!touched) setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          onFocus={() => hasSaved && setOpen(true)}
          autoComplete="off"
          spellCheck={false}
        />
        {hasSaved ? (
          <button
            type="button"
            className="wallet-selector__toggle"
            onClick={() => setOpen(!open)}
            aria-label="Адресная книга"
          >
            <AppleEmoji emoji="📋" size={16} />
          </button>
        ) : (
          <span className="input-icon">
            {showValid ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#26a17b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
              </svg>
            )}
          </span>
        )}
      </div>

      {showError && (
        <div className="form-field__error">{(validation as { reason: string }).reason}</div>
      )}

      {open && hasSaved && (
        <div className="wallet-selector__dropdown">
          <div className="wallet-selector__header">Сохранённые адреса</div>
          {wallets.map((w) => (
            <button
              key={w.id}
              type="button"
              className={
                'wallet-selector__item' +
                (w.address === value ? ' wallet-selector__item--active' : '')
              }
              onClick={() => pick(w.address)}
            >
              <div className="wallet-selector__item-main">
                <div className="wallet-selector__item-label">
                  {w.label || (w.is_default ? 'По умолчанию' : 'Без названия')}
                  {w.is_default && <span className="wallet-selector__default">★</span>}
                </div>
                <div className="wallet-selector__item-address">
                  {w.address.slice(0, 10)}…{w.address.slice(-8)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {enabled && loaded && wallets.length === 0 && showValid && (
        <div className="wallet-selector__hint">
          <AppleEmoji emoji="💾" size={14} /> После оплаты адрес сохранится в вашей адресной книге.
        </div>
      )}
    </div>
  );
}
